import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MapPin, Plus, X, Loader2, Edit2, Calculator } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

interface Rota {
  id: number
  nome: string
  uf_origem: string
  cidade_origem: string | null
  uf_destino: string
  cidade_destino: string | null
  km: number
  valor_base: number
  valor_por_kg: number
  valor_pct_carga: number
  ativa: boolean
}

export default function ExpedicaoRotasPage() {
  const qc = useQueryClient()
  const [editar, setEditar] = useState<Rota | 'new' | null>(null)
  const [calcularRota, setCalcularRota] = useState<Rota | null>(null)

  const { data: rotas = [], isLoading } = useQuery<Rota[]>({
    queryKey: ['rotas-frete'],
    queryFn: async () => (await api.get('/expedicao/rotas')).data,
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="text-brand-600" /> Rotas de Frete
          </h1>
          <p className="text-ink-600 text-sm">Cadastro de rotas com tabela de preços (base + por kg + % sobre carga).</p>
        </div>
        <button onClick={() => setEditar('new')} className="btn-primary">
          <Plus size={14} /> Nova rota
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Nome</th><th>Origem</th><th>Destino</th><th className="text-right">Km</th>
              <th className="text-right">Base</th><th className="text-right">R$/kg</th><th className="text-right">% carga</th><th className="w-32"></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && rotas.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-ink-500">Nenhuma rota.</td></tr>
            )}
            {rotas.map(r => (
              <tr key={r.id}
                onClick={() => setEditar(r)}
                className="hover:bg-violet-50 cursor-pointer">
                <td className="font-semibold">{r.nome}</td>
                <td>{r.uf_origem}{r.cidade_origem ? ` - ${r.cidade_origem}` : ''}</td>
                <td>{r.uf_destino}{r.cidade_destino ? ` - ${r.cidade_destino}` : ''}</td>
                <td className="text-right">{r.km}</td>
                <td className="text-right">{formatMoney(r.valor_base)}</td>
                <td className="text-right">{formatMoney(r.valor_por_kg)}</td>
                <td className="text-right">{r.valor_pct_carga}%</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <button onClick={() => setCalcularRota(r)} className="btn-secondary !py-1 !px-2 text-xs">
                      <Calculator size={12} /> Calcular
                    </button>
                    <button onClick={() => setEditar(r)} className="btn-secondary !py-1 !px-2 text-xs">
                      <Edit2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editar && <RotaModal rota={editar === 'new' ? null : editar} onClose={() => setEditar(null)}
        onDone={() => { setEditar(null); qc.invalidateQueries({ queryKey: ['rotas-frete'] }) }} />}

      {calcularRota && <CalculadoraModal rota={calcularRota} onClose={() => setCalcularRota(null)} />}
    </div>
  )
}

function RotaModal({ rota, onClose, onDone }: { rota: Rota | null; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    nome: rota?.nome || '',
    uf_origem: rota?.uf_origem || 'CE', cidade_origem: rota?.cidade_origem || '',
    uf_destino: rota?.uf_destino || 'PI', cidade_destino: rota?.cidade_destino || '',
    km: rota?.km || 0,
    valor_base: rota?.valor_base || 0,
    valor_por_kg: rota?.valor_por_kg || 0,
    valor_pct_carga: rota?.valor_pct_carga || 0,
  })
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))

  const salvar = useMutation({
    mutationFn: async () => {
      if (rota) return (await api.put(`/expedicao/rotas/${rota.id}`, form)).data
      return (await api.post('/expedicao/rotas', form)).data
    },
    onSuccess: () => { toast.success('Rota salva'); onDone() },
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">{rota ? 'Editar' : 'Nova'} rota</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-xs font-medium mb-1">Nome *</div>
            <input className="input w-full" value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Fortaleza → Teresina" />
          </label>
          <div className="grid grid-cols-4 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">UF origem</div>
              <input className="input w-full" value={form.uf_origem} maxLength={2} onChange={e => set('uf_origem', e.target.value.toUpperCase())} />
            </label>
            <label className="block col-span-3">
              <div className="text-xs font-medium mb-1">Cidade origem</div>
              <input className="input w-full" value={form.cidade_origem} onChange={e => set('cidade_origem', e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">UF destino</div>
              <input className="input w-full" value={form.uf_destino} maxLength={2} onChange={e => set('uf_destino', e.target.value.toUpperCase())} />
            </label>
            <label className="block col-span-3">
              <div className="text-xs font-medium mb-1">Cidade destino</div>
              <input className="input w-full" value={form.cidade_destino} onChange={e => set('cidade_destino', e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Km</div>
              <input type="number" className="input w-full" value={form.km} onChange={e => set('km', parseInt(e.target.value) || 0)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Valor base R$</div>
              <input type="number" step="0.01" className="input w-full" value={form.valor_base} onChange={e => set('valor_base', parseFloat(e.target.value) || 0)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">R$ por kg</div>
              <input type="number" step="0.0001" className="input w-full" value={form.valor_por_kg} onChange={e => set('valor_por_kg', parseFloat(e.target.value) || 0)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">% da carga</div>
              <input type="number" step="0.01" className="input w-full" value={form.valor_pct_carga} onChange={e => set('valor_pct_carga', parseFloat(e.target.value) || 0)} />
            </label>
          </div>
          <div className="text-xs text-ink-500">
            Fórmula: <strong>frete = valor base + (R$/kg × peso) + (% da carga × valor)</strong>
          </div>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={!form.nome || salvar.isPending} onClick={() => salvar.mutate()} className="btn-primary">
            {salvar.isPending && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  )
}

function CalculadoraModal({ rota, onClose }: { rota: Rota; onClose: () => void }) {
  const [peso, setPeso] = useState('100')
  const [valor, setValor] = useState('1000')

  const { data } = useQuery<any>({
    queryKey: ['calc-frete', rota.id, peso, valor],
    queryFn: async () => (await api.post('/expedicao/rotas/calcular', null, {
      params: { rota_id: rota.id, peso_kg: parseFloat(peso) || 0, valor_carga: parseFloat(valor) || 0 }
    })).data,
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">Simulador de frete — {rota.nome}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-xs font-medium mb-1">Peso (kg)</div>
            <input type="number" className="input w-full" value={peso} onChange={e => setPeso(e.target.value)} />
          </label>
          <label className="block">
            <div className="text-xs font-medium mb-1">Valor da carga (R$)</div>
            <input type="number" step="0.01" className="input w-full" value={valor} onChange={e => setValor(e.target.value)} />
          </label>
          {data && (
            <div className="bg-brand-50 border border-brand-200 rounded p-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Base:</span><span>{formatMoney(data.valor_base)}</span></div>
              <div className="flex justify-between"><span>Por peso:</span><span>{formatMoney(data.valor_por_peso)}</span></div>
              <div className="flex justify-between"><span>% carga:</span><span>{formatMoney(data.valor_por_valor_carga)}</span></div>
              <div className="flex justify-between border-t pt-1 font-bold text-base">
                <span>Total:</span><span>{formatMoney(data.total)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
